import * as React from 'react'
import {cva, type VariantProps} from 'class-variance-authority'
import {CircleCheck, CircleX, Info, TriangleAlert} from 'lucide-react'

import {cn} from '@/shared/lib/cn'

const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        destructive:
          'text-destructive bg-card [&>svg]:text-current *:data-[slot=alert-description]:text-destructive/90',
        error:
          'border-signal-error/50 bg-signal-error/10 text-signal-error [&>svg]:text-signal-error',
        success:
          'border-signal-success/50 bg-signal-success/10 text-signal-success [&>svg]:text-signal-success',
        warning:
          'border-signal-warning/50 bg-signal-warning/10 text-signal-warning [&>svg]:text-signal-warning',
        info: 'border-signal-info/50 bg-signal-info/10 text-signal-info [&>svg]:text-signal-info',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

type AlertVariant = NonNullable<VariantProps<typeof alertVariants>['variant']>

const variantIcons: Partial<Record<AlertVariant, React.ReactNode>> = {
  error: <CircleX />,
  success: <CircleCheck />,
  warning: <TriangleAlert />,
  info: <Info />,
}

function Alert({
  className,
  variant,
  children,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  const icon = variant ? variantIcons[variant] : undefined

  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({variant}), className)}
      {...props}
    >
      {icon}
      {children}
    </div>
  )
}

function AlertTitle({className, ...props}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn('col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight', className)}
      {...props}
    />
  )
}

function AlertDescription({className, ...props}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'text-muted-foreground col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed',
        className,
      )}
      {...props}
    />
  )
}

export {Alert, AlertTitle, AlertDescription, alertVariants}
